/* The farm displays: seven interactive sections drawn from DDP Display Bank set 5
   (Methodology/DisplayBank/sets/set-05/index.html), ported to run inside Dawn.
   Wording comes from window.TFACC_FARM (assets/tfacc-farm-data.js, checked against the live pages when it was
   built). Prices, links and stock come live from Shopify, written into each section as JSON (.tfd-live).
   Every display is scoped to its own section root, so two on one page do not collide, and each re-runs when
   the theme editor reloads its section. Under reduced motion each display shows its complete still state. */
(() => {
  if (window.TFD) return;
  const still = matchMedia('(prefers-reduced-motion: reduce)').matches;
  const fine = matchMedia('(hover: hover) and (pointer: fine)').matches;
  const narrow = () => matchMedia('(max-width: 749px)').matches;
  const esc = t => String(t ?? '').replace(/[&<>"]/g, c => ({'&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;'}[c]));
  const quote = (q, withCite = true) => q ? `<blockquote><p>${esc(q.text)}</p>${withCite && q.src ? `<cite>${esc(q.src)}</cite>` : ''}</blockquote>` : '';
  const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  const CDN = window.TFD_CDN || ''; /* empty on the store; a local preview points it at the live domain */
  const img = (name, w = 800) => !name ? '' : /^(https?:)?\/\//.test(name) ? name : `${CDN}/cdn/shop/files/${encodeURI(name)}?width=${w}`;

  /* the data: captured wording, with live Shopify values laid over each product */
  const data = () => {
    const D = window.TFACC_FARM || {};
    const live = {};
    document.querySelectorAll('script.tfd-live').forEach(s => { try { Object.assign(live, JSON.parse(s.textContent)); } catch (e) {} });
    Object.values(D.products || {}).forEach(p => {
      const L = live[p.handle];
      if (!L) return;
      if (L.price) p.price = L.price;
      if (L.url) p.url = L.url;
      p.available = L.available !== false;
    });
    return D;
  };
  const sizeOf = p => (p.label && p.label.size) || p.size;

  const kit = root => {
    const $ = q => root.querySelector(q), $$ = q => [...root.querySelectorAll(q)];
    const r = n => root.querySelector(`[data-r="${n}"]`);
    const uid = (root.dataset.uid || Math.random().toString(36).slice(2)).replace(/[^\w-]/g, '');
    return {$, $$, r, uid};
  };

  /* Run once the reader has the element in front of them: nearly all of it on screen (or as much as fits), held
     there for a beat. Scrolling past quickly does not count, and the next stop on it tries again. Displays that
     would otherwise hide their contents behind a click open themselves this way, so the opening is seen. */
  const onView = (el, fn, ratio = .85, hold = 700) => {
    if (!('IntersectionObserver' in window)) return fn();
    /* The beat starts once, when enough of the element comes into view, and is only cancelled if it drops back out;
       further thresholds crossed while it keeps scrolling into view must not restart it. */
    let timer = 0, armed = false;
    const need = () => Math.min(ratio, (innerHeight * .9) / Math.max(1, el.getBoundingClientRect().height));
    const io = new IntersectionObserver(es => {
      const ok = es[es.length - 1].intersectionRatio >= need() - .001;
      if (ok && !armed) { armed = true; timer = setTimeout(() => { io.disconnect(); fn(); }, hold); }
      else if (!ok && armed) { armed = false; clearTimeout(timer); }
    }, {threshold: [0, .1, .2, .3, .4, .5, .6, .7, .75, .8, .85, .9, .95, 1]});
    io.observe(el);
  };

  const INIT = {};

  /* 1 · the gatefold */
  INIT.gatefold = (root, D) => {
    const {$$, r} = kit(root), P = D.products || {}, G = D.gatefold, gf = r('gf'), btn = r('toggle');
    if (!G || !gf) return;
    const o = root.dataset;
    r('aerial').src = o.aerial || img(G.photos.aerial, 1400);
    r('herbs').src = o.herbs || img(G.photos.herbs, 900);
    r('sheep').src = o.sheep || img(G.photos.sheep, 900);
    r('knit').src = o.knit || img(G.photos.knit, 700);
    r('knit').style.setProperty('--focus', o.knitFocus || G.photos.knit_focus || '50% 20%');
    $$('.gf-title b').forEach(b => { b.textContent = 'The Farm at Catawissa Creek'; });
    $$('.gf-title small').forEach(s => { s.textContent = G.cover_line.text; });
    r('land').textContent = G.land.text;
    r('creek').textContent = G.creek.text;
    r('soil').textContent = G.soil.text;
    r('season').textContent = G.season.text;
    if (o.products !== 'false') {
      r('prods').innerHTML = G.season_products.filter(id => P[id]).map(id => {
        const p = P[id], sized = id === 'me1' || id === 'em4' ? `, ${p.label.size.toLowerCase()}` : '';
        return `<li><img src="${img(p.photo, 200)}" alt="" loading="lazy"><span><b>${esc(p.name + sized)}</b><i>${esc(p.price)}</i></span></li>`;
      }).join('');
    } else if (G.skin_line) {
      /* On a page that already ends with these products: the skincare flap carries a photograph and the
         homepage's own line instead, the way the wool flap does. */
      r('prods').remove(); r('season').remove();
      root.querySelector('.gf-soil').classList.add('gf-photo');
      r('skinfig').hidden = false; r('skintxt').hidden = false;
      r('skin').src = o.skin || img(G.photos.skin, 700);
      r('skin').style.setProperty('--focus', o.skinFocus || G.photos.skin_focus || '50% 50%');
      r('skin').alt = (P.trio && P.trio.alt) || '';
      r('skinline').textContent = G.skin_line.text;
    }
    r('shop').textContent = G.shop.text;
    r('wool').textContent = G.wool.text;
    r('concept').textContent = G.concept.text;
    r('knittext').textContent = G.knit.text;
    r('wait').textContent = G.waitlist.text;
    r('explore').textContent = G.explore.text;
    const openLabel = btn.dataset.open || 'Open the card', closeLabel = btn.dataset.close || 'Close the card';
    const set = open => {
      gf.classList.remove('dragging'); gf.style.removeProperty('--a');
      gf.dataset.open = open; btn.setAttribute('aria-expanded', open);
      btn.textContent = open ? closeLabel : openLabel;
      $$('.gf-in a').forEach(a => { a.tabIndex = open ? 0 : -1; });
    };
    set(still);
    let touched = false;
    btn.addEventListener('click', () => { touched = true; set(gf.dataset.open !== 'true'); });
    if (o.auto !== 'false' && !still) onView(gf, () => { if (!touched && gf.dataset.open !== 'true') set(true); }, .7, 350);
    let drag = null;
    gf.addEventListener('pointerdown', e => {
      if (e.target.closest('a') || e.button > 0) return;
      touched = true;
      drag = {x: e.clientX, open: gf.dataset.open === 'true', moved: false, id: e.pointerId};
    });
    gf.addEventListener('pointermove', e => {
      if (!drag || still || !fine || narrow()) return;
      const dx = Math.abs(e.clientX - drag.x);
      if (!drag.moved && dx < 6) return;
      if (!drag.moved) { drag.moved = true; gf.setPointerCapture(drag.id); gf.classList.add('dragging'); }
      const k = Math.min(1, dx / (gf.offsetWidth / 2));
      gf.style.setProperty('--a', ((drag.open ? 1 - k : k) * 180).toFixed(1));
    });
    const end = e => {
      if (!drag) return;
      if (drag.moved) set((parseFloat(gf.style.getPropertyValue('--a')) || 0) > 70);
      else if (e.type === 'pointerup' && !e.target.closest('a')) set(gf.dataset.open !== 'true');
      drag = null;
    };
    gf.addEventListener('pointerup', end);
    gf.addEventListener('pointercancel', end);
    gf.addEventListener('dragstart', e => e.preventDefault());
  };

  /* 2 · the label, peeled back */
  INIT.label = (root, D) => {
    const {$$, r} = kit(root), P = D.products || {};
    const byHandle = h => Object.keys(P).find(id => P[id].handle === h);
    const only = root.dataset.product ? byHandle(root.dataset.product) : null;
    if (root.dataset.product && !only) { root.hidden = true; return; }  /* a product with no label on file */
    const auto = root.dataset.auto !== 'false';
    const ids = only ? [only] : ['hb', 'em4', 'me4', 'cal', 'cham', 'lav'].filter(id => P[id]);
    const pick = r('pick'), photo = r('photo'), lbl = r('lbl'), front = r('front'), back = r('back'), flap = r('flap'), tab = r('tab');
    const under = root.querySelector('.pl-under'), toggle = r('toggle');
    /* The label keeps its proportions unless its back has more to say than fits; then it grows to fit,
       and the space the folded flap hangs into grows with it. */
    const fit = () => {
      if (still) return;
      [lbl, under].forEach(el => el && el.style.removeProperty('height'));
      const need = back.scrollHeight;
      if (need > lbl.clientHeight + 1) [lbl, under].forEach(el => el && el.style.setProperty('height', `${need}px`));
    };
    if (only) pick.remove();
    else pick.innerHTML = ids.map((id, i) => `<button class="tfd-pill" type="button" data-p="${id}" aria-pressed="${i === 0}"><i style="background:${P[id].label.ink}"></i>${esc(P[id].name)}</button>`).join('');
    let state = 'rest', Pnow = null, raf = 0, drag = null;
    const geom = () => ({W: lbl.clientWidth, H: lbl.clientHeight});
    const restP = (rr = 20) => { const {W} = geom(); return [W - rr, rr]; };
    const openP = () => { const {W, H} = geom(); return [W, 2 * H]; };
    const clip = (poly, M, n, folded) => {
      const s = X => ((X[0] - M[0]) * n[0] + (X[1] - M[1]) * n[1]) * (folded ? -1 : 1), out = [];
      poly.forEach((A, i) => {
        const B = poly[(i + 1) % poly.length], sa = s(A), sb = s(B);
        if (sa <= 0) out.push(A);
        if ((sa < 0 && sb > 0) || (sa > 0 && sb < 0)) { const t = sa / (sa - sb); out.push([A[0] + t * (B[0] - A[0]), A[1] + t * (B[1] - A[1])]); }
      });
      return out;
    };
    const poly = pts => pts.length > 2 ? `polygon(${pts.map(p => `${p[0].toFixed(1)}px ${p[1].toFixed(1)}px`).join(',')})` : 'polygon(0 0,0 0,0 0)';
    const draw = Pp => {
      Pnow = Pp;
      const {W, H} = geom(), dx = W - Pp[0], dy = -Pp[1], len = Math.hypot(dx, dy);
      const tx = drag && drag.moved ? Math.max(26, Math.min(W - 26, Pp[0])) : W - 26, ty = drag && drag.moved ? Math.max(26, Math.min(2 * H - 26, Pp[1])) : state === 'open' ? 2 * H - 26 : 26;
      tab.style.transform = `translate(${tx}px,${ty}px)`;
      if (len < 1) { front.style.clipPath = ''; flap.style.visibility = 'hidden'; return; }
      const n = [dx / len, dy / len], M = [(W + Pp[0]) / 2, Pp[1] / 2], rect = [[0, 0], [W, 0], [W, H], [0, H]];
      front.style.clipPath = poly(clip(rect, M, n, false));
      flap.style.clipPath = poly(clip(rect, M, n, true));
      flap.style.visibility = 'visible';
      const a = 1 - 2 * n[0] * n[0], b = -2 * n[0] * n[1], d = 1 - 2 * n[1] * n[1], md = M[0] * n[0] + M[1] * n[1];
      flap.style.transform = `matrix(${a},${b},${b},${d},${2 * md * n[0]},${2 * md * n[1]})`;
      const deg = Math.atan2(n[0], -n[1]) * 180 / Math.PI, L = W * Math.abs(n[0]) + H * Math.abs(n[1]);
      const s0 = (((M[0] - W / 2) * n[0] + (M[1] - H / 2) * n[1]) / L + .5) * 100;
      flap.style.backgroundImage = `linear-gradient(${deg.toFixed(1)}deg, rgba(70,50,25,.3) ${s0.toFixed(1)}%, rgba(70,50,25,.07) ${(s0 + 2.5).toFixed(1)}%, rgba(255,255,255,.62) ${(s0 + 9).toFixed(1)}%, rgba(255,255,255,0) ${(s0 + 36).toFixed(1)}%)`;
    };
    const animate = (to, ms = 680) => {
      cancelAnimationFrame(raf);
      const from = Pnow.slice(), {W, H} = geom(), via = [W * .12, H * 1.05], t0 = performance.now();
      const swing = Math.hypot(to[0] - from[0], to[1] - from[1]) > H * .8;
      const step = now => {
        const t = Math.min(1, (now - t0) / ms), e = 1 - Math.pow(1 - t, 3), u = 1 - e;
        draw(swing ? [u * u * from[0] + 2 * u * e * via[0] + e * e * to[0], u * u * from[1] + 2 * u * e * via[1] + e * e * to[1]]
                   : [from[0] + (to[0] - from[0]) * e, from[1] + (to[1] - from[1]) * e]);
        if (t < 1) raf = requestAnimationFrame(step);
      };
      raf = requestAnimationFrame(step);
    };
    const setState = (s, instant, ms) => {
      state = s;
      tab.setAttribute('aria-pressed', s === 'open');
      lbl.classList.toggle('is-open', s === 'open');
      tab.setAttribute('aria-label', s === 'open' ? 'Fold the label back down' : 'Lift the label to read what is in it');
      if (toggle) { toggle.textContent = s === 'open' ? 'See the front of the label' : 'Read the back of the label'; toggle.setAttribute('aria-pressed', s === 'open'); }
      const target = s === 'open' ? openP() : restP();
      if (instant || still || !Pnow) draw(target); else animate(target, ms);
    };
    const fill = id => {
      const p = P[id], L = p.label;
      if (photo) { photo.src = img(p.photo, 700); photo.alt = p.alt; }
      front.style.setProperty('--inkc', L.ink);
      front.innerHTML = `<span class="lf-top">${esc(L.top)}</span><span class="lf-name">${esc(L.name)}</span><span class="lf-kind">${esc(L.kind)}</span>${L.extra ? `<span class="lf-extra">${esc(L.extra)}</span>` : ''}<span class="lf-size">${esc(L.size)}</span>`;
      const left = p.left_out || [];
      const leftHTML = left.length > 1 ? `<span class="nots">${left.map(q => `<span>${esc(q.text)}</span>`).join('')}</span>` : esc(left[0] && left[0].text);
      const rows = [['Ingredients', esc(p.ingredients)], ['Not in it', leftHTML], ['How to use', esc(p.use && p.use.text)], ['Made', esc(p.made && p.made.text)]].filter(x => x[1]);
      back.innerHTML = `<dl>${rows.map(([k, v]) => `<dt>${k}</dt><dd>${v}</dd>`).join('')}</dl><p class="lb-foot"><span>${esc(p.size)}</span><b>${esc(p.price)}</b></p>`;
      fit();
    };
    if (!only) pick.addEventListener('click', e => {
      const b = e.target.closest('button'); if (!b) return;
      $$('[data-r="pick"] button').forEach(x => x.setAttribute('aria-pressed', x === b));
      fill(b.dataset.p); setState('rest', true);
      if (auto && !still) { peeled = false; setTimeout(peel, 300); }
    });
    fill(ids[0]);
    /* It folds itself back: once the label is well into view, the corner lifts, then the label peels back. */
    let peeled = false;
    const peel = () => {
      if (peeled || state !== 'rest' || drag) return;
      peeled = true;
      animate(restP(46), 420);
      setTimeout(() => { if (state === 'rest' && !drag) setState('open', false, 1300); }, 560);
    };
    if (still) return;
    setState('rest', true);
    if (toggle) toggle.addEventListener('click', () => { peeled = true; setState(state === 'open' ? 'rest' : 'open', false, 900); });
    if (auto) onView(lbl, peel, .6, 250);
    tab.addEventListener('pointerdown', e => {
      e.preventDefault(); tab.setPointerCapture(e.pointerId); cancelAnimationFrame(raf); peeled = true;
      drag = {x: e.clientX, y: e.clientY, moved: false};
    });
    tab.addEventListener('pointermove', e => {
      if (!drag) return;
      if (!drag.moved && Math.hypot(e.clientX - drag.x, e.clientY - drag.y) < 4) return;
      drag.moved = true;
      const {W, H} = geom(), rc = lbl.getBoundingClientRect();
      draw([Math.max(-W * .2, Math.min(W, e.clientX - rc.left)), Math.max(0, Math.min(2 * H, e.clientY - rc.top))]);
    });
    const up = () => {
      if (!drag) return;
      const moved = drag.moved; drag = null;
      if (!moved) return setState(state === 'open' ? 'rest' : 'open');
      const {W, H} = geom();
      setState(Math.hypot(W - Pnow[0], Pnow[1]) > H * .6 ? 'open' : 'rest');
    };
    tab.addEventListener('pointerup', up);
    tab.addEventListener('pointercancel', up);
    tab.addEventListener('keydown', e => { if (e.key === 'Escape' && state === 'open') setState('rest'); });
    tab.addEventListener('click', e => { if (e.detail === 0) setState(state === 'open' ? 'rest' : 'open'); }); /* Enter or Space; pointer clicks are handled on pointerup */
    tab.addEventListener('pointerenter', () => { if (fine && !drag && state === 'rest') animate(restP(40), 260); });
    tab.addEventListener('pointerleave', () => { if (fine && !drag && state === 'rest') animate(restP(), 260); });
    addEventListener('resize', () => { fit(); draw(state === 'open' ? openP() : restP()); });
  };

  /* 3 · where the herbs grow */
  INIT.rings = (root, D) => {
    const {$, $$, r, uid} = kit(root), P = D.products || {};
    const svg = r('svg'), list = r('list'), read = r('read');
    if (!D.herbs || !svg) return;
    const aerial = root.dataset.aerial || img(D.gatefold.photos.aerial, 800);
    const cx = 320, cy = 320, R0 = 92;
    const band = {farm: [R0, 182, 137], neighbor: [182, 242, 212], other: [242, 300, 271]};
    const ring = Object.fromEntries(D.rings);
    const ANG = {farm: {calendula: 35, comfrey: 90, chamomile: 145, yarrow: 215, bluevervain: 270, mountainmint: 325}, neighbor: {lavender: 180}, other: {calendula: 35, chamomile: 145}};
    const xy = (a, rr) => [cx + rr * Math.sin(a * Math.PI / 180), cy - rr * Math.cos(a * Math.PI / 180)];
    const ids = ['hb', 'em4', 'me4', 'cal', 'cham', 'lav', 'trio'].filter(id => P[id]);
    const clipId = `rgClip-${uid}`;
    let s = `<defs><clipPath id="${clipId}"><circle cx="${cx}" cy="${cy}" r="${R0 - 4}"/></clipPath></defs>
      <circle cx="${cx}" cy="${cy}" r="300" fill="rgba(255,255,255,.55)"/>
      <circle cx="${cx}" cy="${cy}" r="242" fill="rgba(62,63,41,.035)"/>
      <circle cx="${cx}" cy="${cy}" r="182" fill="rgba(62,63,41,.07)"/>
      ${[300, 242, 182].map(rr => `<circle class="ln" cx="${cx}" cy="${cy}" r="${rr}"/>`).join('')}`;
    /* On a phone the ring names are drawn larger relative to the rings, and the longest runs into the herb
       labels beside it, so long names break onto two lines there. */
    [['farm', 137], ['neighbor', 212], ['other', 271]].forEach(([k, rr]) => {
      const name = ring[k].toUpperCase(), y = cy - rr + 4;
      if (narrow() && name.length > 20) {
        const words = name.split(' '); let a = '', b = '';
        words.forEach(w => { (a.length < name.length / 2 ? (a += (a ? ' ' : '') + w) : (b += (b ? ' ' : '') + w)); });
        s += `<text class="ringname" x="${cx}" y="${y - 8}" text-anchor="middle"><tspan x="${cx}">${esc(a)}</tspan><tspan x="${cx}" dy="17">${esc(b)}</tspan></text>`;
      } else s += `<text class="ringname" x="${cx}" y="${y}" text-anchor="middle">${esc(name)}</text>`;
    });
    s += `<g data-r="threads"></g><image class="rg-centre" data-r="centre" clip-path="url(#${clipId})"/>`;
    s += `<circle cx="${cx}" cy="${cy}" r="${R0 - 4}" fill="none" stroke="#FFFFFF" stroke-width="3"/><circle class="ln" cx="${cx}" cy="${cy}" r="${R0 - 1}"/>`;
    Object.entries(ANG).forEach(([rk, herbs]) => Object.entries(herbs).forEach(([h, a]) => {
      const H = D.herbs[h]; if (!H) return;
      const [x, y] = xy(a, band[rk][2]), right = a > 12 && a < 168, left = a > 192 && a < 348;
      const tx = right ? x + 13 : left ? x - 13 : x, ty = right || left ? y + 5 : (a >= 168 && a <= 192 ? y + 25 : y - 15);
      const anchor = right ? 'start' : left ? 'end' : 'middle';
      s += `<g class="rg-dot${H.wild && rk === 'farm' ? ' wild' : ''}" data-h="${h}" data-ring="${rk}" tabindex="0" role="button" aria-label="${esc(H.name)}, ${esc(ring[rk])}">
        <circle class="hit" cx="${x}" cy="${y}" r="22"/><circle class="d" cx="${x}" cy="${y}" r="7"/><text x="${tx}" y="${ty}" text-anchor="${anchor}">${esc(H.name)}</text></g>`;
    }));
    svg.innerHTML = s;
    const imgEl = r('centre'), threads = r('threads');
    const centre = (src, portrait) => {
      imgEl.style.opacity = 0;
      setTimeout(() => {
        const w = portrait ? 2 * R0 : 2 * R0 * 5 / 3, h = portrait ? 3 * R0 : 2 * R0;
        imgEl.setAttribute('href', src); imgEl.setAttribute('width', w); imgEl.setAttribute('height', h);
        imgEl.setAttribute('x', cx - w / 2); imgEl.setAttribute('y', portrait ? cy - h * .62 : cy - h / 2);
        imgEl.style.opacity = 1;
      }, still ? 0 : 180);
    };
    centre(aerial, false);
    list.innerHTML = ids.map(id => `<li><button type="button" data-p="${id}" aria-pressed="false"><img src="${img(P[id].photo, 120)}" alt="" loading="lazy"><span>${esc(P[id].name)}</span></button></li>`).join('');
    const legend = `<div class="legend"><span><i></i>Cultivated</span><span><i class="w"></i>Wild-foraged</span><span><i class="f"></i>In the chosen product</span></div>`;
    read.innerHTML = `${legend}<p class="hint">${esc(root.dataset.hint || 'Pick a product to trace its herbs to where they grow, or pick an herb to see which products carry it.')}</p>`;
    const dots = () => $$('.rg-dot');
    const selectProduct = id => {
      const p = P[id];
      $$('[data-r="list"] button').forEach(b => { b.setAttribute('aria-pressed', b.dataset.p === id); b.classList.remove('has'); });
      centre(img(p.photo, 400), true);
      const on = new Set(p.herbs.map(h => `${h.herb}|${h.ring}`));
      dots().forEach(g => { const k = `${g.dataset.h}|${g.dataset.ring}`; g.classList.toggle('on', on.has(k)); g.classList.toggle('dim', !on.has(k)); });
      threads.innerHTML = p.herbs.filter(h => ANG[h.ring] && ANG[h.ring][h.herb] !== undefined).map((h, i) => {
        const a = ANG[h.ring][h.herb], r1 = R0 + 2, r2 = band[h.ring][2] - 9, [x1, y1] = xy(a, r1), [x2, y2] = xy(a, r2), [qx, qy] = xy(a + 9, (r1 + r2) / 2);
        return `<path class="rg-thread" style="--d:${i * 110}ms" d="M${x1.toFixed(1)} ${y1.toFixed(1)} Q${qx.toFixed(1)} ${qy.toFixed(1)} ${x2.toFixed(1)} ${y2.toFixed(1)}"/>`;
      }).join('');
      [...threads.querySelectorAll('.rg-thread')].forEach(path => {
        const L = path.getTotalLength(); path.style.strokeDasharray = L;
        if (still) return;
        path.style.strokeDashoffset = L; path.getBoundingClientRect();
        requestAnimationFrame(() => { path.style.strokeDashoffset = 0; });
      });
      const groups = [];
      p.herbs.forEach(h => {
        let g = groups.find(x => x.q.text === h.q.text);
        if (!g) groups.push(g = {q: h.q, herbs: [], rings: []});
        if (!g.herbs.includes(h.herb)) g.herbs.push(h.herb);
        if (!g.rings.includes(h.ring)) g.rings.push(h.ring);
      });
      const base = p.base_line ? p.base_line.text : (id === 'cal' || id === 'cham' || id === 'lav') ? p.ingredients : '';
      read.innerHTML = groups.map(g => `<div class="row"><p class="h">${esc(g.herbs.map(h => D.herbs[h].name).join(', '))}</p><p class="r">${esc(g.rings.map(x => ring[x]).join(' and '))}</p>${quote(g.q, false)}</div>`).join('')
        + (base ? `<div class="row"><p class="r">The base</p><blockquote><p>${esc(base)}</p></blockquote></div>` : '')
        + `<div class="row"><a class="button button--secondary" href="${esc(p.url)}">${esc(p.name)}, ${esc(p.price)}</a></div>`;
    };
    const selectHerb = h => {
      const H = D.herbs[h], carry = ids.filter(id => P[id].herbs.some(x => x.herb === h));
      $$('[data-r="list"] button').forEach(b => { b.setAttribute('aria-pressed', 'false'); b.classList.toggle('has', carry.includes(b.dataset.p)); });
      dots().forEach(g => { g.classList.toggle('on', g.dataset.h === h); g.classList.toggle('dim', g.dataset.h !== h); });
      threads.innerHTML = '';
      centre(aerial, false);
      read.innerHTML = `<div class="row"><p class="h">${esc(H.name)}</p><p class="r">${H.wild ? 'Wild-foraged' : 'Cultivated'}</p>${quote(H.where)}</div>
        <div class="row"><p class="r">In</p><div class="in">${carry.map(id => `<button class="tfd-pill" type="button" data-p="${id}">${esc(P[id].name)}</button>`).join('')}</div></div>`;
    };
    list.addEventListener('click', e => { const b = e.target.closest('button'); if (b) selectProduct(b.dataset.p); });
    read.addEventListener('click', e => { const b = e.target.closest('button[data-p]'); if (b) selectProduct(b.dataset.p); });
    svg.addEventListener('click', e => { const g = e.target.closest('.rg-dot'); if (g) selectHerb(g.dataset.h); });
    svg.addEventListener('keydown', e => { const g = e.target.closest('.rg-dot'); if (g && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); selectHerb(g.dataset.h); } });
    const pre = root.dataset.product && ids.find(id => P[id].handle === root.dataset.product);
    if (pre) selectProduct(pre);
  };

  /* 4 · the apothecary chest */
  INIT.chest = (root, D) => {
    const {$$, r} = kit(root), P = D.products || {}, o = root.dataset;
    /* One drawer per product; the sizes of a product share its drawer. Salve and creams on top, the three oils in
       the middle, the trio as the long drawer across the bottom. */
    const GROUPS = [['hb'], ['em4', 'em1'], ['me4', 'me1'], ['cal'], ['cham'], ['lav'], ['trio']]
      .map(g => g.filter(id => P[id])).filter(g => g.length);
    const byId = Object.fromEntries(GROUPS.map(g => [g[0], g]));
    const needs = D.needs || [], needName = Object.fromEntries(needs);
    const chips = r('needs'), box = r('drawers'), counter = r('counter'), chest = root.querySelector('.chest');
    if (!box) return;
    const sizeText = p => ((p.label && p.label.size) || p.size || '').replace(/\.$/, '');
    /* For each need, the drawer's line comes from its first size that names that need, so two sizes saying
       nearly the same thing do not both appear. */
    const lineFor = (g, k) => g.map(id => P[id].needs && P[id].needs[k]).find(Boolean);
    chips.innerHTML = needs.map(([k, n]) => `<button class="tfd-pill" type="button" data-n="${k}" aria-pressed="false">${esc(n)}</button>`).join('');
    box.innerHTML = GROUPS.map(g => {
      const p = P[g[0]], L = p.label, sizes = g.map(id => P[id].label.size).filter(Boolean).join(' · ');
      return `<button class="dr${g[0] === 'trio' ? ' wide' : ''}" type="button" data-g="${g[0]}" aria-pressed="false" aria-label="${esc(p.name)}${sizes ? ', ' + esc(sizes.toLowerCase()) : ''}">
        <span class="dr-front"><span class="dr-card" style="--inkc:${L.ink}"><b>${esc(L.name)}</b>${sizes ? `<small>${esc(sizes)}</small>` : ''}</span><span class="dr-knob"></span></span></button>`;
    }).join('');
    const sizesLine = g => g.map(id => `<a href="${esc(P[id].url)}">${esc(sizeText(P[id]))} · ${esc(P[id].price)}</a>`).join('<span class="sep" aria-hidden="true"></span>');
    const row = (g, i, lines) => {
      const p = P[g[0]];
      return `<li style="--i:${i}"><img src="${img(p.photo, 160)}" alt="" loading="lazy"><div><h4><a href="${esc(p.url)}">${esc(p.name)}</a></h4><p class="sz">${sizesLine(g)}</p>${lines}</div></li>`;
    };
    const restView = () => { counter.innerHTML = `<p class="ct-head">${esc(o.restHead || 'Pull any drawer.')}</p><p class="ct-sub">${esc(o.restSub || 'Or choose what you need it for above.')}</p>`; };
    const show = (openIds, filtering) => {
      $$('.dr').forEach(d => { const on = openIds.includes(d.dataset.g); d.classList.toggle('open', on); d.setAttribute('aria-pressed', on); });
      box.classList.toggle('filtering', filtering);
    };
    const byNeed = k => {
      $$('[data-r="needs"] button').forEach(b => b.setAttribute('aria-pressed', b.dataset.n === k));
      const hit = GROUPS.filter(g => lineFor(g, k));
      show(hit.map(g => g[0]), true);
      counter.innerHTML = `<p class="ct-head">${esc(needName[k])}</p><ul class="ct-list">${hit.map((g, i) => row(g, i, quote(lineFor(g, k), false))).join('')}</ul>`;
    };
    const byDrawer = id => {
      $$('[data-r="needs"] button').forEach(b => b.setAttribute('aria-pressed', 'false'));
      show([id], true);
      /* needs that share a sentence are listed together over it, once; two sizes saying nearly the same sentence
         (the same opening words) count as one, and the fuller of the two is kept */
      const g = byId[id], groups = [];
      const same = (a, b) => a === b || (a.length > 50 && b.length > 50 && a.slice(0, 50) === b.slice(0, 50));
      needs.forEach(([k, n]) => {
        const q = lineFor(g, k); if (!q) return;
        const hit = groups.find(x => same(x.q.text, q.text));
        if (!hit) return groups.push({q, names: [n]});
        hit.names.push(n);
        if (q.text.length > hit.q.text.length) hit.q = q;
      });
      const lines = groups.map(x => `<p class="k">${esc(x.names.join(' · '))}</p>${quote(x.q, false)}`).join('');
      counter.innerHTML = `<p class="ct-head">What it is for</p><ul class="ct-list">${row(g, 0, lines)}</ul>`;
    };
    const reset = () => { $$('[data-r="needs"] button').forEach(b => b.setAttribute('aria-pressed', 'false')); show([], false); restView(); };
    /* The guide: a paper tag hung from the knob of a closed drawer, beside the one that opened by itself, until the
       reader opens one. */
    let tag = null, touched = false;
    const placeTag = () => {
      if (!tag) return;
      const d = box.querySelector(`.dr[data-g="${tag.dataset.g}"]`), knob = d && d.querySelector('.dr-knob');
      if (!knob) return;
      const c = chest.getBoundingClientRect(), k = knob.getBoundingClientRect();
      const x = Math.min(k.right - c.left + 16, c.width - tag.offsetWidth - 8);
      tag.style.left = `${Math.max(8, x)}px`; tag.style.top = `${k.top - c.top + k.height / 2 - tag.offsetHeight / 2}px`;
    };
    const hideTag = () => { if (!tag) return; const t = tag; tag = null; t.classList.remove('on'); setTimeout(() => t.remove(), 450); };
    const showTag = id => {
      tag = document.createElement('span');
      tag.className = 'ch-tag'; tag.setAttribute('aria-hidden', 'true'); tag.dataset.g = id;
      tag.innerHTML = `<b>${esc(o.hint || 'Pull any drawer')}</b>`;
      chest.appendChild(tag); placeTag();
      requestAnimationFrame(() => tag && tag.classList.add('on'));
    };
    addEventListener('resize', placeTag);
    chips.addEventListener('click', e => { const b = e.target.closest('button'); if (!b) return; touched = true; hideTag(); b.getAttribute('aria-pressed') === 'true' ? reset() : byNeed(b.dataset.n); });
    box.addEventListener('click', e => {
      const d = e.target.closest('.dr'); if (!d) return;
      const firstTouch = !touched; touched = true; hideTag();
      /* a second click on the open drawer closes it; the first click, often on the drawer that opened itself, keeps it */
      const single = !$$('[data-r="needs"] button[aria-pressed="true"]').length && d.classList.contains('open');
      single && !firstTouch ? reset() : byDrawer(d.dataset.g);
    });
    restView();
    const first = GROUPS[0][0], next = (GROUPS[1] || GROUPS[0])[0];
    if (still) byDrawer(first);
    else if (o.auto !== 'false') onView(chest, () => { if (!touched) { byDrawer(first); setTimeout(() => { if (!touched) showTag(next); }, 450); } }, .6, 400);
  };

  /* 5 · the wheel of the year */
  INIT.wheel = (root, D) => {
    const {r, uid} = kit(root), P = D.products || {};
    const S = D.seasons || [], svg = r('svg'), btns = r('btns'), panel = r('panel'), all = r('all');
    if (!S.length || !svg) return;
    const now = new Date().getMonth(), seasonOf = m => S.find(s => s.months.includes(m));
    const pt = (a, rr) => [250 + rr * Math.sin(a * Math.PI / 180), 250 - rr * Math.cos(a * Math.PI / 180)];
    const sector = (a1, a2, r1, r2) => { const [x1, y1] = pt(a1, r2), [x2, y2] = pt(a2, r2), [x3, y3] = pt(a2, r1), [x4, y4] = pt(a1, r1);
      return `M${x1} ${y1}A${r2} ${r2} 0 0 1 ${x2} ${y2}L${x3} ${y3}A${r1} ${r1} 0 0 0 ${x4} ${y4}Z`; };
    const tints = {spring: 'rgba(62,63,41,.12)', summer: 'rgba(62,63,41,.2)', fall: 'rgba(62,63,41,.28)', winter: 'rgba(62,63,41,.06)'};
    const marks = S.flatMap(s => s.items.filter(i => i.month !== undefined).map(i => i.month));
    let disc = `<circle cx="250" cy="250" r="240" fill="#F4EFE3"/><circle cx="250" cy="250" r="240" fill="none" stroke="rgba(0,0,0,.12)"/>`;
    S.forEach(s => {
      const a1 = s.months[0] * 30 - 15, a2 = a1 + 90, [sx, sy] = pt(a1, 165), [ex, ey] = pt(a2, 165), arc = `arc-${s.key}-${uid}`;
      disc += `<path d="${sector(a1, a2, 148, 188)}" fill="${tints[s.key]}"/>
        <path id="${arc}" d="M${sx} ${sy}A165 165 0 0 1 ${ex} ${ey}" fill="none"/>
        <text class="sname"><textPath href="#${arc}" startOffset="50%" text-anchor="middle">${esc(s.name.toUpperCase())}</textPath></text>`;
    });
    for (let m = 0; m < 12; m++) {
      const a = m * 30, [tx1, ty1] = pt(a + 15, 192), [tx2, ty2] = pt(a + 15, 238);
      disc += `<line x1="${tx1}" y1="${ty1}" x2="${tx2}" y2="${ty2}" stroke="rgba(62,63,41,.22)"/>
        <text class="month${m === now ? ' now' : ''}" transform="rotate(${a} 250 250)" x="250" y="${250 - 209}">${MONTHS[m].slice(0, 3).toUpperCase()}</text>`;
      if (marks.includes(m)) { const [dx, dy] = pt(a, 197); disc += `<circle cx="${dx}" cy="${dy}" r="3.2" fill="#3E3F29"/>`; }
      if (m === now) { const [nx, ny] = pt(a, 231); disc += `<circle cx="${nx}" cy="${ny}" r="4" fill="none" stroke="#121212" stroke-width="1.5"/>`; }
    }
    const top = `<path d="${sector(-46, 46, 142, 243)}" fill="none" stroke="#FFFFFF" stroke-width="2.5"/>
      <path d="M250 -2 l-9 -14 h18z" fill="#FFFFFF"/>
      <circle cx="250" cy="250" r="140" fill="#FBF8F1" stroke="rgba(0,0,0,.1)"/>
      <circle cx="250" cy="250" r="132" fill="none" stroke="rgba(62,63,41,.18)"/>
      <text class="hub-s" x="250" y="214">THE FARM'S YEAR</text>
      <text class="hub-m" data-r="hubm" x="250" y="262"></text>
      <text class="hub-s" data-r="hubs" x="250" y="290"></text>
      <g data-r="hubnow"><rect x="223" y="303" width="54" height="19" fill="#3E3F29"/><text class="hub-n" x="250" y="316">NOW</text></g>`;
    const shadow = `wlShadow-${uid}`;
    svg.innerHTML = `<defs><filter id="${shadow}" x="-20%" y="-20%" width="140%" height="140%"><feDropShadow dx="0" dy="14" stdDeviation="14" flood-color="#000" flood-opacity=".35"/></filter></defs>
      <g filter="url(#${shadow})"><g class="wl-disc" data-r="disc" tabindex="0" role="slider" aria-label="Month" aria-valuemin="1" aria-valuemax="12">${disc}</g></g>${top}`;
    const dEl = r('disc');
    btns.innerHTML = S.map(s => `<button class="tfd-pill" type="button" data-s="${s.key}" aria-pressed="false">${esc(s.name)}</button>`).join('') + `<button class="tfd-pill" type="button" data-s="now">Today</button>`;
    const panelHTML = s => {
      const items = s.items.map(i => `<li><p class="lab">${esc(i.label)}</p>${quote(i.q)}</li>`).join('');
      const pr = s.product && P[s.product.id];
      const prod = pr ? `<a class="wl-prod" href="${esc(pr.url)}"><img src="${img(pr.photo, 120)}" alt="" loading="lazy"><div><b>${esc(pr.name)}</b><span>${esc(pr.price)}</span>${quote(s.product.q)}</div></a>` : '';
      return `<figure class="wl-print"><img src="${img(s.photo, 600)}" alt="" loading="lazy">${s.photo_name ? `<figcaption>${esc(s.photo_name)}</figcaption>` : ''}</figure>
        <div><div class="wl-name"><h3>${esc(s.name)}</h3>${s.months.includes(now) ? '<span class="wl-now">Now</span>' : ''}</div><ul class="wl-items">${items}</ul>${prod}</div>`;
    };
    if (still) all.innerHTML = S.map(s => `<div class="wl-panel">${panelHTML(s)}</div>`).join('');
    let R = -now * 30, shown = null;
    const month = () => ((Math.round(-R / 30) % 12) + 12) % 12;
    const render = () => {
      dEl.style.transform = `rotate(${R}deg)`;
      const m = month(), s = seasonOf(m);
      r('hubm').textContent = MONTHS[m];
      r('hubs').textContent = s.name.toUpperCase();
      r('hubnow').style.display = m === now ? '' : 'none';
      dEl.setAttribute('aria-valuenow', m + 1); dEl.setAttribute('aria-valuetext', `${MONTHS[m]}, ${s.name}`);
      [...btns.querySelectorAll('button')].forEach(b => b.setAttribute('aria-pressed', b.dataset.s === s.key));
      if (shown !== s.key) {
        shown = s.key;
        panel.innerHTML = `<div class="wl-panel${still ? '' : ' swap'}">${panelHTML(s)}</div>`;
      }
    };
    const goMonth = m => { const cur = month(); let delta = ((m - cur) % 12 + 12) % 12; if (delta > 6) delta -= 12; R -= delta * 30; render(); };
    render();
    btns.addEventListener('click', e => { const b = e.target.closest('button'); if (!b) return; goMonth(b.dataset.s === 'now' ? now : S.find(s => s.key === b.dataset.s).months[1]); });
    dEl.addEventListener('keydown', e => {
      if (['ArrowRight', 'ArrowDown'].includes(e.key)) { e.preventDefault(); R -= 30; render(); }
      if (['ArrowLeft', 'ArrowUp'].includes(e.key)) { e.preventDefault(); R += 30; render(); }
    });
    if (still) return;
    let drag = null;
    const ang = e => { const rc = svg.getBoundingClientRect(), sc = 528 / rc.width, x = (e.clientX - rc.left) * sc - 14, y = (e.clientY - rc.top) * sc - 26; return Math.atan2(x - 250, -(y - 250)) * 180 / Math.PI; };
    dEl.addEventListener('pointerdown', e => { drag = {a: ang(e), R}; dEl.setPointerCapture(e.pointerId); dEl.classList.add('dragging'); });
    dEl.addEventListener('pointermove', e => {
      if (!drag) return;
      let d = ang(e) - drag.a; if (d > 180) d -= 360; if (d < -180) d += 360;
      R = drag.R + d; drag.R = R; drag.a = ang(e); render();
    });
    const up = () => { if (!drag) return; drag = null; dEl.classList.remove('dragging'); R = Math.round(R / 30) * 30; render(); };
    dEl.addEventListener('pointerup', up);
    dEl.addEventListener('pointercancel', up);
  };

  /* 6 · the yarn line */
  INIT.yarn = (root, D) => {
    const {r, uid} = kit(root), Y = D.yarn;
    if (!Y) return;
    const st = Y.stages, nowI = Math.max(0, st.findIndex(s => s.state === 'now'));
    const track = r('track'), svg = r('svg'), card = r('card');
    let xs = [], yAt = () => 0, ballX = 0, cur = nowI, w = 0;
    const done = `ynDone-${uid}`, ahead = `ynAhead-${uid}`;
    const media = s => {
      if (s.ewes) return `<div class="yn-media ewes">${s.ewes.map(e => `<figure><img src="${img(e.photo, 300)}" alt="${esc(e.name)}, one of the farm's merino ewes" loading="lazy"><figcaption>${esc(e.name)}</figcaption></figure>`).join('')}</div>`;
      if (s.concept) return `<div class="yn-media knit">${s.photos.map(p => `<img src="${img(p, 300)}" alt="A garment from Anne's concept collection" loading="lazy">`).join('')}<figcaption>${esc(s.concept.text)}</figcaption></div>`;
      if (s.photos) return `<div class="yn-media one"><img src="${img(s.photos[0], 800)}" alt="" loading="lazy"></div>`;
      return `<div class="yn-num" aria-hidden="true">${st.indexOf(s) + 1}</div>`;
    };
    const tag = s => s.state === 'now' ? '<span class="tag now">Now</span>' : s.state === 'ahead' ? '<span class="tag">Ahead</span>' : '';
    /* A step either quotes the farm's own page (q) or, where the farm has told us the page is out of date, says it
       plainly (line), with no page credited. */
    const cardHTML = s => `${media(s)}<div class="yn-text">${tag(s)}<h3>${esc(s.label)}</h3>${quote(s.q)}${s.line ? `<p class="line">${esc(s.line)}</p>` : ''}${s.note ? `<p class="note">${esc(s.note.text)}</p>` : ''}</div>`;
    if (root.dataset.waitlist !== 'false') r('end').innerHTML = `<div><p class="tfd-cap">${esc(Y.coming.text)}</p><p class="w">${esc(Y.waitlist.text)}</p><p class="h">${esc(Y.heirlooms.text)}</p></div>
      <a class="button button--primary" href="mailto:${esc(Y.email)}">${esc(root.dataset.cta || 'Email to join the waitlist')}</a>`;
    if (still) r('all').innerHTML = st.map((s, i) => `<article><p class="tag">${i + 1}${s.state === 'now' ? ' · Now' : s.state === 'ahead' ? ' · Ahead' : ''}</p><h4>${esc(s.label)}</h4><p>${esc((s.q && s.q.text) || s.line || '')}</p></article>`).join('');
    const ball = () => svg.querySelector('.yn-ball');
    const show = i => {
      const changed = i !== cur || !card.innerHTML; cur = i;
      [...svg.querySelectorAll('.yn-st')].forEach(g => g.classList.toggle('on', +g.dataset.i === i));
      const b = ball(); b.setAttribute('aria-valuenow', i + 1); b.setAttribute('aria-valuetext', st[i].label);
      r('count').textContent = `Step ${i + 1} of ${st.length}`;
      if (changed) { const s = st[i]; card.className = `yn-card${s.ewes || s.photos ? '' : ' text'}${still ? '' : ' swap'}`; card.innerHTML = cardHTML(s); }
    };
    const place = (x, live) => {
      ballX = Math.max(xs[0], Math.min(xs[xs.length - 1], x));
      const by = yAt(ballX), b = ball();
      b.setAttribute('transform', `translate(${ballX},${by - 17})`);
      b.querySelector('.wraps').setAttribute('transform', `rotate(${(ballX / 17 * 180 / Math.PI) % 360})`);
      svg.querySelector(`#${done} rect`).setAttribute('width', Math.min(ballX, xs[nowI]));
      const aR = svg.querySelector(`#${ahead} rect`); aR.setAttribute('x', xs[nowI]); aR.setAttribute('width', Math.max(0, ballX - xs[nowI]));
      let i = 0; xs.forEach((v, k) => { if (Math.abs(v - ballX) < Math.abs(xs[i] - ballX)) i = k; });
      if (i !== cur || !live) show(i);
    };
    const glide = i => {
      const from = ballX, to = xs[i], t0 = performance.now(), ms = still ? 0 : 420;
      const step = t1 => { const t = ms ? Math.min(1, (t1 - t0) / ms) : 1, e = 1 - Math.pow(1 - t, 3); place(from + (to - from) * e, true); if (t < 1) requestAnimationFrame(step); else show(i); };
      requestAnimationFrame(step);
    };
    const bindBall = () => {
      const b = ball();
      let drag = null;
      b.addEventListener('pointerdown', e => { drag = {x: e.clientX, b: ballX}; b.setPointerCapture(e.pointerId); e.preventDefault(); });
      b.addEventListener('pointermove', e => { if (!drag) return; const rc = svg.getBoundingClientRect(); place(drag.b + (e.clientX - drag.x) * (w / rc.width), true); });
      const up = () => { if (!drag) return; drag = null; glide(cur); };
      b.addEventListener('pointerup', up); b.addEventListener('pointercancel', up);
      b.addEventListener('keydown', e => {
        if (['ArrowRight', 'ArrowUp'].includes(e.key)) { e.preventDefault(); glide(Math.min(st.length - 1, cur + 1)); }
        if (['ArrowLeft', 'ArrowDown'].includes(e.key)) { e.preventDefault(); glide(Math.max(0, cur - 1)); }
      });
    };
    const layout = () => {
      w = track.clientWidth;
      if (!w) return;
      const h = track.clientHeight, pad = Math.min(56, w * .07), y = 92, n = st.length;
      xs = st.map((_, i) => pad + i * (w - 2 * pad) / (n - 1));
      let d = `M${xs[0]} ${y}`;
      for (let i = 1; i < n; i++) { const a = xs[i - 1], b = xs[i], k = i % 2 ? -10 : 10, dx = (b - a) * .36; d += ` C${a + dx} ${y + k} ${b - dx} ${y + k} ${b} ${y}`; }
      svg.setAttribute('viewBox', `0 0 ${w} ${h}`);
      svg.innerHTML = `<defs><clipPath id="${done}"><rect x="0" y="0" height="${h}" width="0"/></clipPath><clipPath id="${ahead}"><rect x="0" y="0" height="${h}" width="0"/></clipPath></defs>
        <path class="yn-guide" d="${d}"/><path class="yn-solid" d="${d}" clip-path="url(#${done})"/><path class="yn-ahead" d="${d}" clip-path="url(#${ahead})"/>
        <g class="yn-flag" transform="translate(${xs[nowI]},${y})"><line x1="0" y1="-38" x2="0" y2="-58"/><rect x="-24" y="-80" width="48" height="22"/><text x="0" y="-65">NOW</text></g>
        ${st.map((s, i) => `<g class="yn-st${i > nowI ? ' ahead' : ''}" data-i="${i}"><circle class="hit" cx="${xs[i]}" cy="${y}" r="22"/><circle class="dot" cx="${xs[i]}" cy="${y}" r="5"/>
          <text x="${i === 0 ? xs[i] - 10 : i === n - 1 ? xs[i] + 10 : xs[i]}" style="text-anchor:${i === 0 ? 'start' : i === n - 1 ? 'end' : 'middle'}" y="${narrow() ? y + 40 : i % 2 ? y + 62 : y + 38}">${esc(s.label)}</text></g>`).join('')}
        <g class="yn-ball" tabindex="0" role="slider" aria-label="Step in the wool's journey" aria-valuemin="1" aria-valuemax="${n}">
          <circle class="body" r="17"/><g class="wraps"><path d="M-13 -5 Q0 -15 13 -5"/><path d="M-16 2 Q0 -8 16 2"/><path d="M-13 9 Q0 1 13 9"/><path d="M-6 -16 Q4 0 -6 16"/></g></g>`;
      const path = svg.querySelector('.yn-guide');
      const L = path.getTotalLength(), pts = [];
      for (let i = 0; i <= 240; i++) { const p = path.getPointAtLength(L * i / 240); pts.push([p.x, p.y]); }
      yAt = x => { for (let i = 1; i < pts.length; i++) if (pts[i][0] >= x) { const [x0, y0] = pts[i - 1], [x1, y1] = pts[i]; return y0 + (y1 - y0) * ((x - x0) / Math.max(1e-6, x1 - x0)); } return y; };
      bindBall();
      place(xs[cur], false);
    };
    svg.addEventListener('click', e => { const g = e.target.closest('.yn-st'); if (g) glide(+g.dataset.i); });
    r('prev').addEventListener('click', () => glide(Math.max(0, cur - 1)));
    r('next').addEventListener('click', () => glide(Math.min(st.length - 1, cur + 1)));
    card.innerHTML = '';
    layout();
    let rt = 0; addEventListener('resize', () => { clearTimeout(rt); rt = setTimeout(layout, 120); });
  };

  /* 7 · the gift bag */
  INIT.giftbag = (root, D) => {
    const {$$, r} = kit(root), P = D.products || {}, G = D.gift, gb = r('gb'), bag = r('bag');
    if (!G || !gb) return;
    const items = G.contents.filter(id => P[id]);
    const spots = () => narrow()
      ? [[-66, -424, -3], [66, -430, 3], [-66, -208, -2], [66, -214, 2]]
      : [[-300, -200, -8], [-104, -262, -3], [100, -262, 3], [296, -200, 8]];
    const place = () => {
      const sp = spots();
      $$('.gb-item').forEach((el, i) => { el.style.setProperty('--x', `${sp[i][0]}px`); el.style.setProperty('--y', `${sp[i][1]}px`); el.style.setProperty('--r', `${sp[i][2]}deg`); });
    };
    r('items').innerHTML = items.map((id, i) => `<a class="gb-item" style="--i:${i}" href="${esc(P[id].url)}" tabindex="-1"><img src="${img(P[id].photo, 300)}" alt="${esc(P[id].alt)}" loading="lazy"><span class="gb-cap"><b>${esc(P[id].name)}</b><span>${esc(P[id].price)}</span></span></a>`).join('');
    const L = G.lounge.text;
    const part = (from, to) => { const a = L.indexOf(from); return a < 0 ? '' : L.slice(a, to ? L.indexOf(to, a) + to.length : undefined); };
    r('tag').innerHTML = `<b>HARVEST</b><span>${esc(part('Awards Week', 'Awards Week'))}</span><span>${esc(part('Beverly Hills', 'Beverly Hills'))}</span>`;
    r('photo').src = root.dataset.photo || img(G.photo, 700);
    r('photocap').textContent = part('Sept. 10', 'Emmy Awards week');
    const showShop = root.dataset.shop !== 'false';
    r('caption').innerHTML = `${root.dataset.label !== 'false' ? `<h3>${esc(G.label.text)}</h3>` : ''}${quote(G.sets)}${showShop ? `<a class="button button--primary" href="${esc(G.collection_url)}">${esc(G.shop.text)}</a>` : ''}`;
    const set = open => {
      gb.dataset.open = open; bag.setAttribute('aria-expanded', open);
      bag.setAttribute('aria-label', open ? 'Put everything back in the bag' : 'Open the gift bag');
      $$('.gb-item').forEach(a => { a.tabIndex = open ? 0 : -1; });
    };
    place();
    set(still);
    let touched = false;
    bag.addEventListener('click', () => { touched = true; set(gb.dataset.open !== 'true'); });
    if (root.dataset.auto !== 'false' && !still) onView(bag, () => { if (!touched && gb.dataset.open !== 'true') set(true); }, .6, 300);
    addEventListener('resize', place);
  };

  const init = scope => {
    const D = data();
    (scope || document).querySelectorAll('[data-tfd]').forEach(root => {
      if (root.dataset.tfdReady) return;
      root.dataset.tfdReady = '1';
      if (still) root.classList.add('tfd-still');
      try { (INIT[root.dataset.tfd] || (() => {}))(root, D); } catch (e) { console.error('tfacc display', root.dataset.tfd, e); }
    });
  };
  window.TFD = {init};
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init()); else init();
  document.addEventListener('shopify:section:load', e => init(e.target));
})();
